//@ts-nocheck
import {
  AuthenticatorProps,
  theme,
  WithAuthenticator,
  WithAuthenticatorProps,
} from '@konecorp/ui-library'
import { Box, CssBaseline } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import React, {
  act,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom'

import Context from '../../context'
import Loading from '../../components/Loading'
import LandingPage from '../LandingPage'
import ProfilePage from '../ProfilePage'
import Qualification from '../Qualification'
import AdminLandingPage from '../AdminLandingPage'
import ScrollableTable from '../../components/ScrollableTable'
import SignOutForm from '../../components/SignOutForm'
import { Redirect } from 'react-router-dom'
import Settings from '../Settings'
import { logUserAction } from '../../utils/apiUtils'
import { CONSTANTS } from '../../CONSTANTS'
import PassportQualificationForm from '../../components/PassportQualificationForm'
import TermsAndCondition from '../../components/TermsAndConditions'
import { clearAllDrafts } from '../../components/BulkOnboardingGrid/draftStorage'
import axios from 'axios'
import i18n from '../../i18n'
import { useGetToken } from '../../hooks/useGetToken'

const {
  REACT_APP_IAPP_URL: APP_URL = '',
  REACT_APP_API_ENDPOINT: API_ENDPOINT = '',
  REACT_APP_EXT_CLIENT_ID: EXT_CLIENT_ID = '',
  REACT_APP_CLIENT_ID: CLIENT_ID = '',
  REACT_APP_TENANT_ID: TENANT_ID = '',
  REACT_APP_SUPERVISOR_USERS: SUPERVISOR_USERS = '',
  REACT_APP_SUPER_ADMIN_USERS: SUPER_ADMIN_USERS = '',
} = process.env

const useStyles = makeStyles()((theme) => ({
  loadingBackdrop: {
    zIndex: 1500,
  },
  toolbar: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
  },
  footer: {
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: '100%',
    '& span.Mui-selected': {
      fontSize: 12,
    },
  },
}))
let isExternalUser=false

const KoneEmployeeApp = (
  props: PropsWithChildren<WithAuthenticatorProps>,
): JSX.Element => {
  const {
    authState: { isAuthenticated ,user},
    updateAuthState,
    updateAcquireToken,
    setUserPreference,
    isLoading,
  } = useContext(Context)
  const [getToken] = useGetToken()
  const [isLanguageHydrated, setIsLanguageHydrated] = useState(false)
  const [email, setEmail] = useState()
  const regex = /^[a-zA-Z0-9._%+-]+@kone\.com$/
  const navigate = useNavigate();
  const location = useLocation()
  const classes = useStyles(theme)
  // const [userRole] = useGetCurrentUserRole();
  const redirectUrl = window.sessionStorage.getItem('redirectUrl')

  const [referrerUrl, setReferrerUrl] = useState(
    window.sessionStorage.getItem('referrerUrl'),
  )

  /* This needed because Azure AD perform redirection and we need to know
     what was an URL user want to access
  */
  useEffect(() => {
    const { pathname, search } = location

    if (pathname.length > 1 && !isAuthenticated) {
      window.sessionStorage.setItem('redirectUrl', `${pathname}${search}`)
    }
    if (pathname.length === 1 && isAuthenticated) {
      if (redirectUrl) {
        navigate(redirectUrl)
      }
    }
    if (pathname.length > 1 && isAuthenticated) {
      window.sessionStorage.removeItem('redirectUrl')
    }
  }, [location, isAuthenticated, navigate, redirectUrl])

  useEffect(() => {
    const updateLoginData = async (): Promise<void> => {
      try {
        const authResponse = await props.acquireToken(true)
        updateAcquireToken(() => props.acquireToken) //store the function

        // The authResponse (try to call refresh token) can fail in slow network or offline mode
        // which is the reason of ? and passing in empty string. However, this following function, updateGraphData
        // is the one who set authState.isAuthenticated to true or false, if the user already logged-in and there is a cache
        // it will result in a true to isAuthenticated, if there is NO cache aka user never login before, it will force user to login like usual
        // read the updateGraphData detail implementation and service-worker.js for more information
        await props.updateGraphData(authResponse?.accessToken || '')
        const authState = props.getAuthState()
        updateAuthState(authState)
        await logUserAction(authResponse?.accessToken,authState.user.username, CONSTANTS.USER_ACTION.LOGIN)
        isExternalUser=!authState.user.username.includes('@kone.com')

        setEmail(authState.user.username)
      } catch (error) {
        console.error('updateLoginData', error)
      }
    }

    if (
      !referrerUrl &&
      document.referrer &&
      process.env.REACT_APP_IAPP_URL !== document.referrer
    ) {
      const url = document.referrer

      setReferrerUrl(url)
      window.sessionStorage.setItem('referrerUrl', url)
    }
    if (!isAuthenticated) updateLoginData()
  }, [isAuthenticated, referrerUrl, props, updateAuthState, updateAcquireToken])

  // Hydrate the user's saved language from the backend once after login, and apply it before
  // any routed screen renders. This makes every screen (not just Settings) render in the
  // correct language on any device - including one with no cached language in localStorage.
  useEffect(() => {
    // Reset the gate whenever auth drops (sign-out) or a new authenticated run begins, so a
    // re-login (or login as a different user) without a full reload re-blocks routes until the
    // new language has been hydrated, instead of rendering with the previous user's language.
    setIsLanguageHydrated(false)
    if (!isAuthenticated) return
    if (!user?.username) {
      setIsLanguageHydrated(true)
      return
    }
    let cancelled = false
    const hydrateLanguage = async (): Promise<void> => {
      try {
        const token = await getToken()
        const response = await axios.get(`${API_ENDPOINT}/v1/userSetting`, {
          headers: { 'Content-Type': 'application/json', Authorization: token },
          params: { id: user.username },
        })
        const lang = response.data?.[0]?.language
        if (!cancelled && response.data?.[0]) {
          const pref = response.data[0]
          setUserPreference((prev) => ({
            ...prev,
            ...(lang ? { languagePreference: lang } : {}),
            businessType: pref.businessType ?? null,
            area: pref.area ?? null,
            frontline: pref.frontline ?? null,
            country: pref.country ?? null,
            supplierType: pref.supplierType ?? null,
          }))
          if (lang) {
            await i18n.changeLanguage(lang)
          }
        }
      } catch (err: any) {
        // Log only the message/status - never the full Axios error, whose config.headers
        // contains the Authorization bearer token and must not leak into console/log collectors.
        const status = err?.response?.status
        console.error(
          `Failed to hydrate user language preference${status ? ` (HTTP ${status})` : ''}: ${err?.message || String(err)}`,
        )
      } finally {
        if (!cancelled) setIsLanguageHydrated(true)
      }
    }
    hydrateLanguage()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, user?.username])

  /**
   * Sign out, dropping anything this browser was holding for the session first.
   *
   * The bulk grid auto-saves an unsent batch to localStorage keyed by username,
   * so without this it is offered back at the next sign-in - to whoever that is,
   * and against whatever access they hold. Rows entered under a wider scope come
   * back with every country they can no longer resolve marked red, which reads
   * as a broken grid rather than as someone else's work.
   */
  const handleSignOut = () => {
    clearAllDrafts()
    props.onSignOut(APP_URL)
  }

  return (
    <>
      {(isLoading || !isAuthenticated || !isLanguageHydrated) && (
        <Loading backdropClassName={classes.loadingBackdrop} />
      )}
      {isAuthenticated && isLanguageHydrated && (
        <>
          <Box pb={11}>
            <CssBaseline />
            <main className={classes.content}>
            <Routes>
              {/* <Route path='/test' element={<Qualification />} /> */}
              <Route path="/signOut" element={<SignOutForm />} />
              <Route path="/profile/*"  element={<ProfilePage onSignOut={handleSignOut}/>} />
              <Route path="/qualification" element={<PassportQualificationForm onSignOut={handleSignOut}/>} />
              <Route path="/admin" element={(() => <AdminLandingPage onSignOut={handleSignOut} />)()} />
              <Route path="/settings" element={<Settings />} />
              {/* <Route path="/" element={<PassportQualificationForm onSignOut={handleSignOut} />} /> */}
              <Route path="/terms" element={<TermsAndCondition  onSignOut={handleSignOut}/> } />
              <Route path="/" element={<Navigate to="/terms" />} />

             </Routes>
            </main>
          </Box>
        </>
      )}
    </>
  )
}

export { KoneEmployeeApp as PureComponent }
export default WithAuthenticator(KoneEmployeeApp, {
  clientId: isExternalUser? EXT_CLIENT_ID : CLIENT_ID,
  authority:`https://login.microsoftonline.com/${TENANT_ID}`,
  redirectUri: APP_URL,
  validateAuthority: true,
  postLogoutRedirectUri: APP_URL,
  navigateToLoginRequestUrl: false,
} as AuthenticatorProps)
